import type { Maybe } from '@adornis/base/utilTypes.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { getSchoolIDsForRoles } from './getSchoolIDsForRoles.js';
import { Role } from '../roles.js';
import type { UserWithPermissionFieldsRoles } from '../UserWithPermissionFields.js';

/**
 * The schools a user may hand out to somebody else - when creating a user or when picking the kostenstelle of a
 * contract. `null` means "no restriction", the picker keeps offering every school.
 *
 * Only schulleitung and sekretariat are narrowed down: the publish filter deliberately publishes every school to
 * every user (see `martinusPublishFilter`), so without this they would be offered foreign schools the server then
 * rejects. Without a valid school context they get an empty list rather than a free pass.
 */
export async function getSelectableSchoolIDs(user: Maybe<UserWithPermissionFieldsRoles>): Promise<string[] | null> {
  if (!user) return null;

  const isUnrestricted = user.roles?.some(
    role => role.name === Role.ADMIN || (role.name === Role.PSB && !!role.contexts?.includes(GLOBAL_CONTEXT)),
  );
  if (isUnrestricted) return null;

  if (!user.roles?.some(role => role.name === Role.SCHULLEITER || role.name === Role.SEKRETAER)) return null;

  // a school scoped psb may create users too, so its schools join the list instead of being cut off by a sekretär role
  return getSchoolIDsForRoles(user, [Role.SCHULLEITER, Role.SEKRETAER, Role.PSB]);
}
